import { strapiGet, strapiPut } from './client';
import {
  TeamsApiResponse,
  TeamsQueryParams,
  TeamsQueryResult,
  Team,
  TeamAttributes,
} from '../types/team.types';
import { normalizeTeamsResponse, transformTeamAttributes } from '../utils/normalizers';
import {
  convertAdvancedFiltersToStrapi,
  buildStrapiQueryString,
  convertFiltersToStrapiQuery,
} from '../utils/filters';
import { AdvancedFilterConfig } from '../../types/filter';
import { StrapiSingleResponse } from '../types/strapi.types';

/**
 * Endpoint base para equipos
 */
const TEAMS_ENDPOINT = '/api/equipos';

/**
 * Obtiene la lista de equipos desde Strapi
 * Retorna los datos en formato crudo de Strapi
 */
export async function fetchTeamsRaw(params?: TeamsQueryParams): Promise<TeamsApiResponse> {
  const queryString = buildTeamsQueryString(params);
  return strapiGet<TeamsApiResponse>(TEAMS_ENDPOINT, queryString);
}

/**
 * Obtiene la lista de equipos normalizada
 * Transforma los datos de Strapi a formato plano
 */
export async function fetchTeams(params?: TeamsQueryParams): Promise<TeamsQueryResult> {
  const response = await fetchTeamsRaw(params);
  return normalizeTeamsResponse(response);
}

/**
 * Obtiene equipos usando filtros del formato AdvancedFilterConfig
 * (El formato que usa el FilterPopup de la tabla)
 */
export async function fetchTeamsWithAdvancedFilters(
  advancedFilters: AdvancedFilterConfig[],
  params?: Omit<TeamsQueryParams, 'filters'>,
): Promise<TeamsQueryResult> {
  const strapiFilters = convertAdvancedFiltersToStrapi(advancedFilters);

  const queryString = buildStrapiQueryString({
    filters: strapiFilters,
    sort: params?.sortField,
    sortDirection: params?.sortDirection,
    page: params?.page,
    pageSize: params?.pageSize,
  });

  const response = await strapiGet<TeamsApiResponse>(TEAMS_ENDPOINT, queryString);
  return normalizeTeamsResponse(response);
}

/**
 * Construye el query string para la petición de equipos
 */
function buildTeamsQueryString(params?: TeamsQueryParams): string {
  if (!params) {
    return '';
  }

  const { page, pageSize, sortField, sortDirection, filters, search } = params;

  // Convertir filtros de TeamFilter a StrapiFilter
  let strapiFilters = {};
  if (filters && filters.length > 0) {
    strapiFilters = convertFiltersToStrapiQuery(filters);
  }

  // Si hay búsqueda, agregar filtro de nombre
  if (search && search.trim()) {
    strapiFilters = {
      ...strapiFilters,
      nombre: {
        $containsi: search.trim(),
      },
    };
  }

  return buildStrapiQueryString({
    filters: strapiFilters,
    sort: sortField,
    sortDirection,
    page,
    pageSize,
  });
}

/**
 * Datos para actualizar un equipo
 */
export interface UpdateTeamData {
  nombre?: string;
  color?: string;
}

/**
 * Actualiza un equipo en Strapi
 * @param id - ID del equipo
 * @param data - Datos a actualizar
 */
export async function updateTeam(id: number, data: UpdateTeamData): Promise<Team> {
  const response = await strapiPut<StrapiSingleResponse<TeamAttributes>>(
    `${TEAMS_ENDPOINT}/${id}`,
    { data },
  );
  return transformTeamAttributes(response.data.id, response.data.attributes);
}

/**
 * Actualiza el nombre de un equipo
 * @param id - ID del equipo
 * @param nombre - Nuevo nombre
 */
export async function updateTeamName(id: number, nombre: string): Promise<Team> {
  return updateTeam(id, { nombre });
}

/**
 * Key factory para las queries de equipos
 * Útil para invalidación y gestión de cache
 */
export const teamsQueryKeys = {
  all: ['teams'] as const,
  lists: () => [...teamsQueryKeys.all, 'list'] as const,
  list: (params?: TeamsQueryParams) => [...teamsQueryKeys.lists(), params] as const,
  details: () => [...teamsQueryKeys.all, 'detail'] as const,
  detail: (id: number) => [...teamsQueryKeys.details(), id] as const,
};
